import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { type SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/Button';
import {
  checkLoginCode,
  requestResetCode,
  startInvite,
  type CodeFlowFailure,
} from '@/app/auth/services/code-flow.service';

type CodePurpose = 'invite' | 'reset';

interface CodeStepProps {
  email: string;
  purpose: CodePurpose;
  onCode: (code: string) => void;
  onBack: () => void;
}

const failureMessageKey = (failure: CodeFlowFailure) => {
  if (failure === 'invalid_code') {
    return 'login.invalidCode' as const;
  }
  if (failure === 'locked') {
    return 'login.codeLocked' as const;
  }
  return 'login.statusFailed' as const;
};

const RESEND_COOLDOWN_SECONDS = 60;

export const CodeStep = ({ email, purpose, onCode, onBack }: CodeStepProps) => {
  const { t } = useTranslation('app');
  const [code, setCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<CodeFlowFailure | null>(null);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (resendIn === 0) {
      return;
    }
    const timer = setTimeout(() => {
      setResendIn((remaining) => remaining - 1);
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [resendIn]);

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsChecking(true);
    setError(null);
    void (async () => {
      const result = await checkLoginCode({
        email,
        code,
        purpose: purpose === 'invite' ? 'invite' : 'password_reset',
      });
      setIsChecking(false);
      if (result.ok) {
        onCode(code);
      } else {
        setError(result.failure);
      }
    })();
  };

  const handleResend = () => {
    setIsSending(true);
    setResendIn(RESEND_COOLDOWN_SECONDS);
    void (async () => {
      if (purpose === 'invite') {
        await startInvite(email);
      } else {
        await requestResetCode(email);
      }
      setIsSending(false);
    })();
  };

  return (
    <form className="grid gap-1 max-w-md mx-auto" onSubmit={handleSubmit}>
      <button
        className="inline-flex items-center gap-1 text-sm text-ink-subtle"
        onClick={onBack}
        type="button"
      >
        <ArrowLeft aria-hidden size={14} />
        {t('login.back')}
      </button>

      <p className="mt-4 text-sm text-ink-subtle">
        {purpose === 'invite' ? t('login.inviteCodeSent') : t('login.resetCodeSent')}
      </p>

      <label className="field mt-2">
        <span>{t('login.code')}</span>
        <div className="field-input">
          <ShieldCheck aria-hidden className="field-icon" size={18} />
          <input
            autoComplete="one-time-code"
            // eslint-disable-next-line jsx-a11y/no-autofocus -- code entry is the only action on this step
            autoFocus
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
            }}
            pattern="\d{6}"
            required
            value={code}
          />
        </div>
      </label>

      {error ? <p className="notice danger mt-2">{t(failureMessageKey(error))}</p> : null}

      <Button
        className="mt-6"
        color="primary"
        disabled={isChecking || code.length !== 6}
        fullWidth
        type="submit"
      >
        {t('login.verifyCode')}
      </Button>

      <button
        className="inline-flex items-center justify-center gap-1 mt-2 text-sm text-ink-subtle"
        disabled={isSending || resendIn > 0}
        onClick={handleResend}
        type="button"
      >
        {isSending
          ? t('login.resending')
          : resendIn > 0
            ? t('login.resendIn', { seconds: resendIn })
            : t('login.resendCode')}
      </button>
    </form>
  );
};
